import React, { useEffect, useState, useCallback } from "react";
import { DeleteFilled } from "@ant-design/icons";
import {
  addFileQuestionnaireClient,
  getListeFileQuestionnaire,
  updateFileQuestionnaireClient,
} from "../../../../../../data/file";
import swal from "sweetalert";
import { DOWNLOAD_FILE_CLIENT } from "../../../../../../api/apiRoutes";
import { cleanAndNormalizeName } from "../../../../../utilities/Utilities";
import { Form, Formik } from "formik";
import { Button, Col, Divider, Input, Row, Space, Typography } from "antd";
import FormLabel from "../../../../../common/Form/FormLabel";
import FormError from "../../../../../common/Form/FormError";
import * as Yup from "yup";
import {
  getFilenameWithoutExtension,
  getIconForFileType,
} from "../../../../../layout/menu/CustomMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye } from "@fortawesome/free-solid-svg-icons";
import { appTheme } from "../../../../../../config/theme";

const { Title } = Typography;

/**
 * Schéma de validation Yup pour l'ajout d'un nouveau fichier
 */
const validationSchemaAdd = Yup.object({
  libelle: Yup.string().required("Le nom du fichier est obligatoire"),
  reference: Yup.string().required("La référence du fichier est obligatoire"),
  selectedFiles: Yup.mixed()
    .required("Veuillez sélectionner un fichier")
    .test("fileSize", "Le fichier doit être inférieur à 10MB", (value) => {
      return !value || value.size <= 10 * 1024 * 1024;
    }),
});

/**
 * Schéma de validation Yup pour la modification (fichier facultatif)
 */
const validationSchemaEdit = Yup.object({
  libelle: Yup.string().required("Le nom du fichier est obligatoire"),
  reference: Yup.string().required("La référence du fichier est obligatoire"),
  selectedFiles: Yup.mixed()
    .nullable()
    .test("fileSize", "Le fichier doit être inférieur à 10MB", (value) => {
      return !value || value.size <= 10 * 1024 * 1024;
    }),
});

/**
 * Composant pour le téléchargement de questionnaires manuscrits
 * Gère l'upload, la validation et l'affichage des fichiers
 */
const FileUploadQuestionnaire = ({
  setIsFileAdd,
  isFileAdd,
  exercice,
  codeSommaire,
}) => {
  // États locaux
  const [loading, setLoading] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  /**
   * Récupère les documents existants pour l'exercice
   */
  const fetchDocuments = useCallback(async (exercice_id, codeSommaire) => {
    try {
      const res = await getListeFileQuestionnaire(exercice_id, codeSommaire);
      if (res?.result && res.result[0]) {
        setQuestionnaire(res.result[0]);
      } else {
        setQuestionnaire(null);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des documents", error);
      setQuestionnaire(null);
    }
  }, []);

  // Charge les documents au montage du composant
  useEffect(() => {
    if (codeSommaire && exercice?.id) {
      fetchDocuments(exercice.id, codeSommaire);
    }
  }, [codeSommaire, exercice, fetchDocuments]);

  // Gestion simplifiée de l'état d'édition
  useEffect(() => {
    // Seulement si on passe de "ajouté" à "non ajouté" ET qu'on a un questionnaire
    if (!isFileAdd && questionnaire && !isEditing) {
      setIsEditing(true);
    }
    // Reset de l'édition quand le fichier est ajouté avec succès
    if (isFileAdd && isEditing) {
      setIsEditing(false);
    }
  }, [isFileAdd, questionnaire, isEditing]);

  /**
   * Gère l'upload du fichier
   */
  const handleUploadAll = useCallback(
    async (values) => {
      setLoading(true);

      const file = values.selectedFiles;

      // Si on est en mode édition et qu'il n'y a pas de nouveau fichier, on garde l'ancien
      let extension = "";
      let fileSize = 0;

      if (file) {
        extension = file.name.split(".").pop().toLowerCase();
        fileSize = file.size;
      }

      // Prépare les données pour l'upload
      const formData = new FormData();
      if (file) {
        formData.append("file", file);
        formData.append("size", fileSize);
        formData.append("extension", extension);
      }
      formData.append("reference", values.reference);
      formData.append("libelle", values.libelle);
      formData.append("description", values.description);
      formData.append("exercice_id", exercice.id);
      formData.append("client_id", exercice.client_id);
      formData.append("codeSommaire", codeSommaire);

      // En mode édition, ajouter l'ID du questionnaire pour la mise à jour
      if (isEditing && questionnaire) {
        formData.append("id", questionnaire.id);
      }

      try {
        let response;

        if (isEditing && questionnaire) {
          // Mode modification
          formData.append("id", questionnaire.id);
          response = await updateFileQuestionnaireClient(formData);
        } else {
          // Mode création
          response = await addFileQuestionnaireClient(formData);
        }

        if (response.status === "success") {
          await swal("Opération effectuée avec succès", "", "success");
          // Recharger les documents après upload réussi
          await fetchDocuments(exercice.id, codeSommaire);
          // Mettre à jour les états après succès
          setIsFileAdd(true);
          setIsEditing(false);
        } else {
          await swal(
            response.message || "Erreur lors de l'upload",
            "",
            "error"
          );
        }
      } catch (error) {
        console.error("Erreur lors de l'upload:", error);
        await swal("Erreur lors du téléchargement", "", "error");
      } finally {
        setLoading(false);
      }
    },
    [
      exercice,
      codeSommaire,
      fetchDocuments,
      setIsFileAdd,
      isEditing,
      questionnaire,
    ]
  );

  /**
   * Télécharge un document existant
   */
  const downloadDocument = useCallback(
    (client_bog, client_lea, fichier) => {
      // Vérifications de sécurité
      if (!exercice || !exercice.mission || !exercice.annee) {
        console.error("Données d'exercice manquantes pour le téléchargement");
        return;
      }

      if (!fichier) {
        console.error("Nom de fichier manquant");
        return;
      }

      const clientBogName = cleanAndNormalizeName(client_bog || "");
      const clientName = cleanAndNormalizeName(client_lea || "");
      const missionName = cleanAndNormalizeName(exercice.mission);

      const url = `${DOWNLOAD_FILE_CLIENT}/${clientBogName}/${clientName}/${exercice.annee}/${missionName}/${fichier}`;
      window.open(url, "_blank");
    },
    [exercice]
  );

  /**
   * Gère le passage en mode édition
   */
  const handleEditMode = useCallback(() => {
    setIsFileAdd(false);
    setIsEditing(true);
  }, [setIsFileAdd]);

  /**
   * Gère l'annulation de l'édition
   */
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    if (questionnaire) {
      setIsFileAdd(true);
    }
  }, [questionnaire, setIsFileAdd]);

  /**
   * Valeurs initiales du formulaire
   */
  const initialValues = {
    selectedFiles: null,
    reference: questionnaire?.reference || `${codeSommaire}-00X`,
    libelle: questionnaire?.libelle || "",
    description: questionnaire?.description || "",
    client_id: exercice?.client_id || "",
    mission_id: exercice?.id || "",
  };

  /**
   * Vérifie si la mission est clôturée
   */
  const isMissionClosed =
    exercice?.isCloture === 1 || exercice?.isClotureProvisoire === 1;

  /**
   * Rendu du composant de consultation pour mission clôturée
   */
  const renderClosedMissionView = () => (
    <div className="d-flex flex-column align-items-center">
      <Title
        style={{
          fontWeight: "600",
          color: appTheme.colors.success,
          textAlign: "center",
        }}
        level={4}
      >
        Cette mission a été clôturée !
      </Title>

      {questionnaire && (
        <Space>
          <Button
            onClick={() =>
              downloadDocument(
                questionnaire?.Bog_Client?.nom_cabinet,
                questionnaire?.Lea_Client?.denomination,
                questionnaire?.fichier
              )
            }
            icon={<FontAwesomeIcon icon={faEye} />}
            type="primary"
          >
            Consulter le formulaire soumis
          </Button>

          <Button
            onClick={handleEditMode}
            icon={<FontAwesomeIcon icon={faEdit} />}
            type="default"
          >
            Modifier
          </Button>
        </Space>
      )}
    </div>
  );

  /**
   * Rendu du composant de confirmation après upload
   */
  const renderSuccessView = () => (
    <div className="d-flex flex-column align-items-center">
      <Title
        style={{
          fontWeight: "600",
          color: appTheme.colors.success,
          textAlign: "center",
        }}
        level={4}
      >
        Vous avez soumis avec succès le formulaire de questionnaire manuscrit !
      </Title>

      <Space>
        <Button
          onClick={() =>
            downloadDocument(
              questionnaire?.Bog_Client?.nom_cabinet,
              questionnaire?.Lea_Client?.denomination,
              questionnaire?.fichier
            )
          }
          icon={<FontAwesomeIcon icon={faEye} />}
          type="primary"
        >
          Consulter
        </Button>

        <Button
          onClick={handleEditMode}
          icon={<FontAwesomeIcon icon={faEdit} />}
          type="default"
        >
          Modifier
        </Button>
      </Space>
    </div>
  );

  /**
   * Rendu du formulaire d'upload
   */
  const renderUploadForm = () => (
    <Formik
      initialValues={initialValues}
      onSubmit={handleUploadAll}
      validationSchema={isEditing ? validationSchemaEdit : validationSchemaAdd}
      enableReinitialize={true}
    >
      {({ values, handleSubmit, setFieldValue, touched, errors }) => (
        <Form>
          <Title
            style={{
              fontWeight: "600",
              color: appTheme.colors.primary_blue,
              textAlign: "center",
            }}
            level={3}
          >
            Formulaire de questionnaire manuscrit
          </Title>

          <Divider />

          <div
            className="mx-auto"
            style={{ maxWidth: "600px", padding: "20px" }}
          >
            <Row gutter={[16, 16]}>
              {/* Sélection de fichier */}
              <Col span={24}>
                <FormLabel label="Fichier" required={!isEditing} />
                <div className="d-flex align-items-center gap-2">
                  <input
                    id="fileInput"
                    type="file"
                    className="form-control"
                    style={{ flex: 1 }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      setFieldValue("selectedFiles", file || null);
                      if (file) {
                        setFieldValue(
                          "libelle",
                          getFilenameWithoutExtension(file.name)
                        );
                      } else {
                        setFieldValue("libelle", "");
                      }
                    }}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteFilled />}
                    onClick={() => {
                      setFieldValue("selectedFiles", null);
                      setFieldValue("libelle", "");
                      const fileInput = document.getElementById("fileInput");
                      if (fileInput) {
                        fileInput.value = "";
                      }
                    }}
                  />
                </div>
                <div className="text-muted mt-1">
                  Taille maximale: 10MB. Formats acceptés: PDF, DOC, DOCX, XLS,
                  XLSX, TXT, JPG, JPEG, PNG
                </div>
                {touched.selectedFiles && errors.selectedFiles && (
                  <FormError fieldName="selectedFiles" />
                )}

                {/* Aperçu du fichier */}
                {values.selectedFiles && (
                  <div className="mt-2 p-2 bg-light rounded">
                    {getIconForFileType(values.selectedFiles.name)}{" "}
                    {values.selectedFiles.name}
                  </div>
                )}
              </Col>

              {/* Référence */}
              <Col span={24}>
                <FormLabel label="Référence" required />
                <Input
                  placeholder="Saisir la référence"
                  value={values.reference}
                  onChange={(e) => setFieldValue("reference", e.target.value)}
                />
                {touched.reference && errors.reference && (
                  <FormError fieldName="reference" />
                )}
              </Col>

              {/* Nom du fichier */}
              <Col span={24}>
                <FormLabel label="Nom du fichier" required />
                <Input
                  placeholder="Le nom du fichier"
                  value={values.libelle}
                  onChange={(e) => setFieldValue("libelle", e.target.value)}
                />
                {touched.libelle && errors.libelle && (
                  <FormError fieldName="libelle" />
                )}
              </Col>

              {/* Description */}
              <Col span={24}>
                <FormLabel label="Note" />
                <Input.TextArea
                  placeholder="Notes..."
                  value={values.description}
                  onChange={(e) => setFieldValue("description", e.target.value)}
                  showCount
                  maxLength={2000}
                  rows={4}
                />
              </Col>

              {/* Boutons */}
              <Col span={24}>
                <Divider />
                {isEditing ? (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Button block size="large" onClick={handleCancelEdit}>
                        Annuler
                      </Button>
                    </Col>
                    <Col span={12}>
                      <Button
                        type="primary"
                        block
                        size="large"
                        loading={loading}
                        onClick={handleSubmit}
                      >
                        {loading ? "Modification..." : "Modifier"}
                      </Button>
                    </Col>
                  </Row>
                ) : (
                  <Button
                    type="primary"
                    block
                    size="large"
                    loading={loading}
                    onClick={handleSubmit}
                  >
                    {loading ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                )}
              </Col>
            </Row>
          </div>
        </Form>
      )}
    </Formik>
  );

  // Rendu conditionnel principal
  return (
    <div className="w-100">
      {isMissionClosed
        ? renderClosedMissionView()
        : isFileAdd && questionnaire
        ? renderSuccessView()
        : renderUploadForm()}
    </div>
  );
};

export default FileUploadQuestionnaire;
